import { put, takeLatest, select, take } from "redux-saga/effects";
import {
  fetchDeviceSuccess,
  fetchDeviceFailure,
  singleDeviceHeaderTypes
} from "../actions/singleDeviceHeader";
import { fetchRequest, devicesTypes } from "../actions/devices";

// Attributes to hide from the Explorer regardless of device type
const BLACKLISTED_ATTRIBUTES = new Set(['advPacket']);

function deriveGATAttributes(device) {
  const mainFields = Object.values(device?.modelmetadata?.main_chart?.fields ?? {});
  const bottomFields = Object.values(device?.modelmetadata?.bottom_charts ?? {});
  const attributes = [...mainFields, ...bottomFields]
    .filter(f => !BLACKLISTED_ATTRIBUTES.has(f.name))
    .map(f => ({
      value: f.name,
      label: f.displayname,
      unit: f.measurement_unit ?? '',
      yMin: f.y_min ?? null,
      yMax: f.y_max ?? null,
    }));
  return attributes;
}

function deriveBLEAttributes(bleDevice, allDevices) {
  const parentGAT = allDevices.find(d => d.uid === bleDevice.parentdeviceid);
  const sensorEntry = parentGAT?.fields?.ble?.sensorList
    ?.find(s => String(s.serialNum) === String(bleDevice.serial));

  const allGATFields = {
    ...(parentGAT?.modelmetadata?.main_chart?.fields ?? {}),
    ...(parentGAT?.modelmetadata?.bottom_charts ?? {}),
  };
  const schemaByName = Object.fromEntries(
    Object.values(allGATFields).map(f => [f.name, f])
  );

  // Keys to skip — not telemetry attributes
  const FIELDS_EXCLUDE = new Set(['location', 'PlaceholderFieldName', ...BLACKLISTED_ATTRIBUTES]);
  const SENSOR_EXCLUDE = new Set(['serialNum', 'rssi', ...BLACKLISTED_ATTRIBUTES]);

  const attributes = [];

  // Always scan bleDevice.fields directly for scalar values (rssi, and any others)
  const bleFields = bleDevice.fields ?? {};
  Object.keys(bleFields).forEach(k => {
    if (FIELDS_EXCLUDE.has(k)) return;
    if (typeof bleFields[k] === 'object' && bleFields[k] !== null) return; // skip nested objects
    const schemaKey = k === 'rssi' ? 'signalStrength' : k;
    const schema = schemaByName[schemaKey];
    attributes.push({
      value: k,
      label: schema?.displayname ?? k,
      unit: schema?.measurement_unit ?? (k === 'rssi' ? 'dBm' : ''),
      yMin: schema?.y_min ?? null,
      yMax: schema?.y_max ?? null,
    });
  });

  // If paired to a GAT, also pick up telemetry from sensorList
  if (sensorEntry) {
    const existingKeys = new Set(attributes.map(a => a.value));
    Object.keys(sensorEntry)
      .filter(k => !SENSOR_EXCLUDE.has(k) && !existingKeys.has(k))
      .forEach(k => {
        const schema = schemaByName[k];
        attributes.push({
          value: k,
          label: schema?.displayname ?? k,
          unit: schema?.measurement_unit ?? '',
          yMin: schema?.y_min ?? null,
          yMax: schema?.y_max ?? null,
        });
      });
  }

  return attributes;
}

function* fetchSingleDeviceHeaderSaga(action) {
  try {
    let allDevices = yield select(state => state.devices.data.devices);

    if (allDevices.length === 0) {
      const filters = yield select(state => state.devices.filters);
      yield put(fetchRequest(filters));
      yield take(devicesTypes.FETCH_SUCCESS);
      allDevices = yield select(state => state.devices.data.devices);
    }

    const device = allDevices.find(d => d.uid === action.payload.id);

    if (!device) {
      yield put(fetchDeviceFailure("Device not found in loaded list"));
      return;
    }

    const isBLE = device.kind === "ts.device.ble";
    const bleAttributes = isBLE ? deriveBLEAttributes(device, allDevices) : [];
    const gatewayAttributes = isBLE ? [] : deriveGATAttributes(device);

    yield put(fetchDeviceSuccess({
      isBLE,
      bleAttributes,
      gatewayAttributes,
      deviceData: [
        {
          name: device.name,
          orgId: device.customdata?.orgId ?? null,
          organizationName: device.customdata?.organizationName ?? "UNKNOWN",
          siteId: device.customdata?.siteId ?? null,
          locationName: device.customdata?.locationName ?? "UNKNOWN",
          healthStatus: device.customdata?.healthStatus,
          connectionStatus: device.customdata?.connectionStatus,
          assetStatus: device.customdata?.assetStatus,
          activeStatus: device.customdata?.activeStatus,
          refid: device.refid,
          gatewayId: device.parentdeviceid ?? "",
          gatewayName: device.customdata?.gatewayName ?? "",
          lastupdated: device.lastupdated,
        },
      ],
    }));
  } catch (error) {
    console.log("error", error);
    yield put(fetchDeviceFailure(error.message));
  }
}

export function* SingleDeviceHeaderSaga() {
  yield takeLatest(
    singleDeviceHeaderTypes.FETCH_DEVICE_REQUEST,
    fetchSingleDeviceHeaderSaga
  );
}